// services/uploadService.js
import axios from 'axios';
import { store } from '../app/store.js';
import {
  uploadStarted,
  uploadProgress,
  uploadCompleted,
  uploadFailed,
  setError
} from '../app/redux/slices/uploadsSlice';

const API_URL =  'https://app.polarcut.io/api';
// const API_URL = 'http://localhost:5410/api';
/**
 * Service to handle direct-to-S3 uploads using multipart uploads.
 *
 * External URL import (API only): To submit a public URL and get file_id + result page URL,
 * use the External API with your organization API key (manage in Org Settings):
 *   POST https://app.polarcut.io/api/external/submit-url
 *   Headers: X-API-Key: <your-org-api-key>
 *   Body: { "url": "https://example.com/video.mp4" }
 *   Returns: { file_id, result_page_url, status: "file_received" }
 *   Status polling: GET /api/external/tasks/:fileId
 *   List all tasks: GET /api/external/tasks
 * External statuses: file_received | file_downloaded | operation_in_progress | reels_ready | error
 */
class UploadService {
  /**
   * Upload a file directly to S3 using multipart upload
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Optional progress callback
   * @param {string} source - Source of the upload (direct_upload, gdrive, dropbox, onedrive)
   * @returns {Promise<Object>} Upload result with file_id and s3_url
   */
  static async uploadFile(file, onProgress = null, source = 'direct_upload') {
    try {
      // Step 1: Initiate multipart upload
      const initiateResponse = await axios.post(`${API_URL}/upload/multipart/initiate`, {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        source: source
      });
      
      const { file_id, upload_id, s3_key, s3_url } = initiateResponse.data;
      
      // Dispatch action to store to indicate upload has started
      store.dispatch(uploadStarted({
        fileId: file_id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        source,
        uploadId: upload_id
      }));
      
      // Step 2: Calculate optimal part size based on file size
      let partSize = this._calculatePartSize(file.size);
      const numParts = Math.ceil(file.size / partSize);
      let uploadedBytes = 0;
      const uploadedParts = [];
      
      // Save upload state to allow resuming if needed
      this.saveUploadState(file_id, upload_id, [], file.name);
      
      // Step 3: Upload each part
      for (let partNumber = 1; partNumber <= numParts; partNumber++) {
        // Update progress
        if (onProgress) {
          const overallProgress = (uploadedBytes / file.size) * 100;
          onProgress(Math.round(overallProgress));
        }
        
        // Update progress in Redux
        store.dispatch(uploadProgress({
          fileId: file_id,
          progress: Math.round((uploadedBytes / file.size) * 100),
          completedParts: [...uploadedParts]
        }));
        
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const partData = file.slice(start, end);
        
        // Get presigned URL for this part
        const partUrlResponse = await axios.get(
          `${API_URL}/upload/multipart/part-url/${file_id}`,
          { 
            params: { 
              upload_id,
              part_number: partNumber
            } 
          }
        );
        
        const { presigned_url } = partUrlResponse.data;
        
        try {
          // Upload the part directly to S3
          const uploadPartResponse = await axios.put(presigned_url, partData, {
            headers: {
              'Content-Type': 'application/octet-stream'
            },
            onUploadProgress: (progressEvent) => {
              if (onProgress) {
                // Calculate progress including previously uploaded parts
                const partProgress = progressEvent.loaded / progressEvent.total;
                const additionalBytes = progressEvent.loaded;
                const overallProgress = (
                  (uploadedBytes + additionalBytes) / file.size
                ) * 100;
                onProgress(Math.round(overallProgress));
                
                // Update progress in Redux
                store.dispatch(uploadProgress({
                  fileId: file_id,
                  progress: Math.round(overallProgress)
                }));
              }
            }
          });
          
          // Save the ETag for this part
          const eTag = uploadPartResponse.headers.etag || uploadPartResponse.headers['ETag'];
          const cleanETag = eTag.replace(/"/g, ''); // Remove quotes from ETag
          
          uploadedParts.push({
            PartNumber: partNumber,
            ETag: cleanETag
          });
          
          // Update upload state with completed parts
          this.saveUploadState(file_id, upload_id, uploadedParts, file.name);
          
          // Update uploaded bytes for progress calculation
          uploadedBytes += (end - start);
          
          // Update progress in Redux with completed parts
          store.dispatch(uploadProgress({
            fileId: file_id,
            progress: Math.round((uploadedBytes / file.size) * 100),
            completedParts: [...uploadedParts]
          }));
        } catch (error) {
          console.error(`Error uploading part ${partNumber}:`, error);
          
          // Save the state with completed parts to allow resuming
          this.saveUploadState(file_id, upload_id, uploadedParts, file.name);
          
          // Dispatch failure to Redux
          store.dispatch(uploadFailed({
            fileId: file_id,
            error: `Failed to upload part ${partNumber}: ${error.message}`
          }));
          
          // Add file_id and upload_id to the error to allow proper cleanup
          error.file_id = file_id;
          error.upload_id = upload_id;
          throw error;
        }
      }
      
      // Step 4: Complete the multipart upload
      const completeResponse = await axios.post(`${API_URL}/upload/multipart/complete/${file_id}`, {
        upload_id,
        parts: uploadedParts
      });
      
      // Clear the saved state since upload is complete
      this.clearUploadState(file_id);
      
      // Dispatch success to Redux
      store.dispatch(uploadCompleted({
        fileId: file_id,
        s3Url: s3_url
      }));
      
      return {
        file_id,
        s3_url,
        upload_id,
        parts_count: numParts
      };
    } catch (error) {
      console.error('Error in multipart upload:', error);
      
      // If we have a file_id and upload_id, try to abort the multipart upload
      if (error.file_id && error.upload_id) {
        try {
          await this.abortMultipartUpload(error.file_id, error.upload_id);
          
          // Dispatch failure to Redux
          store.dispatch(uploadFailed({
            fileId: error.file_id,
            error: error.message || 'Upload failed and was aborted'
          }));
        } catch (abortError) {
          console.error('Error aborting multipart upload:', abortError);
        }
      }
      
      // Dispatch global error
      store.dispatch(setError(
        error.response?.data?.error ||
        error.message ||
        'Failed to upload file. Please try again.'
      ));
      
      throw new Error(
        error.response?.data?.error ||
        'Failed to upload file. Please try again.'
      );
    }
  }
  
  /**
   * Abort a multipart upload in case of failure
   * @param {string} fileId - File ID
   * @param {string} uploadId - Multipart upload ID
   * @returns {Promise<void>}
   */
  static async abortMultipartUpload(fileId, uploadId) {
    await axios.post(`${API_URL}/upload/multipart/abort/${fileId}`, {
      upload_id: uploadId
    });
  }
  
  /**
   * Calculate optimal part size based on file size
   * @param {number} fileSize - File size in bytes
   * @returns {number} Part size in bytes
   * @private
   */
  static _calculatePartSize(fileSize) {
    // S3 supports up to 10000 parts, each between 5MB and 5GB
    const MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_PARTS = 10000;
    
    // For files under 50MB, use the minimum part size
    if (fileSize < 50 * 1024 * 1024) {
      return MIN_PART_SIZE;
    }
    
    // For larger files, calculate a part size that will keep us under the MAX_PARTS limit
    // but also make reasonably sized chunks
    let partSize = Math.ceil(fileSize / MAX_PARTS);
    
    // Round up to the nearest MB for cleaner sizes
    const MB = 1024 * 1024;
    partSize = Math.ceil(partSize / MB) * MB;
    
    // Ensure the part size is at least the minimum
    return Math.max(partSize, MIN_PART_SIZE);
  }
  
  /**
   * Resume a multipart upload that was interrupted
   * @param {File} file - The file to upload
   * @param {string} fileId - Existing file ID
   * @param {string} uploadId - Existing upload ID
   * @param {Array} completedParts - Array of parts already uploaded
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} Upload result
   */
  static async resumeMultipartUpload(file, fileId, uploadId, completedParts = [], onProgress = null) {
    try {
      // Dispatch action to store to indicate upload is being resumed
      store.dispatch(uploadStarted({
        fileId: fileId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        source: 'resumed',
        uploadId: uploadId,
        initialProgress: completedParts.length > 0 ? completedParts.length / Math.ceil(file.size / this._calculatePartSize(file.size)) * 100 : 0
      }));
      
      // Get status of existing upload
      const statusResponse = await axios.get(`${API_URL}/upload/multipart/status/${fileId}`, {
        params: { upload_id: uploadId }
      });
      
      const { s3_key, s3_url } = statusResponse.data;
      
      // Calculate part information
      const partSize = this._calculatePartSize(file.size);
      const numParts = Math.ceil(file.size / partSize);
      let uploadedBytes = 0;
      
      // Convert completedParts to Map for easy lookup
      const completedPartsMap = new Map(
        completedParts.map(part => [part.PartNumber, part])
      );
      
      // Calculate how many bytes we've already uploaded
      completedParts.forEach(part => {
        const partNumber = part.PartNumber;
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        uploadedBytes += (end - start);
      });
      
      // Update progress with already uploaded parts
      if (onProgress) {
        const overallProgress = (uploadedBytes / file.size) * 100;
        onProgress(Math.round(overallProgress));
      }
      
      // Upload remaining parts
      for (let partNumber = 1; partNumber <= numParts; partNumber++) {
        // Skip already completed parts
        if (completedPartsMap.has(partNumber)) {
          continue;
        }
        
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const partData = file.slice(start, end);
        
        // Get presigned URL for this part
        const partUrlResponse = await axios.get(
          `${API_URL}/upload/multipart/part-url/${fileId}`,
          { 
            params: { 
              upload_id: uploadId,
              part_number: partNumber
            } 
          }
        );
        
        const { presigned_url } = partUrlResponse.data;
        
        // Upload the part directly to S3
        const uploadPartResponse = await axios.put(presigned_url, partData, {
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const partProgress = progressEvent.loaded / progressEvent.total;
              const overallProgress = (
                (uploadedBytes + progressEvent.loaded) / file.size
              ) * 100;
              onProgress(Math.round(overallProgress));
            }
          }
        });
        
        // Save the ETag for this part
        const eTag = uploadPartResponse.headers.etag || uploadPartResponse.headers['ETag'];
        completedPartsMap.set(partNumber, {
          PartNumber: partNumber,
          ETag: eTag.replace(/"/g, '') // Remove quotes from ETag
        });
        
        uploadedBytes += (end - start);
        
        // Save updated state
        this.saveUploadState(fileId, uploadId, Array.from(completedPartsMap.values()), file.name);
      }
      
      // Complete the multipart upload with all parts
      const allParts = Array.from(completedPartsMap.values())
        .sort((a, b) => a.PartNumber - b.PartNumber);
        
      const completeResponse = await axios.post(`${API_URL}/upload/multipart/complete/${fileId}`, {
        upload_id: uploadId,
        parts: allParts
      });
      
      // Clear saved state
      this.clearUploadState(fileId);
      
      // At the end, dispatch completion
      store.dispatch(uploadCompleted({
        fileId: fileId,
        s3Url: s3_url
      }));
      
      return {
        file_id: fileId,
        s3_url,
        upload_id: uploadId,
        parts_count: numParts
      };
    } catch (error) {
      // Dispatch failure
      store.dispatch(uploadFailed({
        fileId: fileId,
        error: error.message || 'Failed to resume upload'
      }));
      
      throw error;
    }
  }
  
  /**
   * Save the state of an in-progress upload to local storage
   * @param {string} fileId - File ID
   * @param {string} uploadId - Upload ID
   * @param {Array} completedParts - Array of completed parts
   * @param {string} fileName - Original filename
   */
  static saveUploadState(fileId, uploadId, completedParts, fileName) {
    try {
      localStorage.setItem(`multipart_upload_${fileId}`, JSON.stringify({
        file_id: fileId,
        upload_id: uploadId,
        completed_parts: completedParts,
        file_name: fileName,
        last_updated: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving upload state:', error);
    }
  }
  
  /**
   * Get saved upload state from local storage
   * @param {string} fileId - File ID
   * @returns {Object|null} Upload state object or null if not found
   */
  static getUploadState(fileId) {
    try {
      const savedState = localStorage.getItem(`multipart_upload_${fileId}`);
      return savedState ? JSON.parse(savedState) : null;
    } catch (error) {
      console.error('Error getting upload state:', error);
      return null;
    }
  }
  
  /**
   * Clear a saved upload state from local storage
   * @param {string} fileId - File ID
   */
  static clearUploadState(fileId) {
    try {
      localStorage.removeItem(`multipart_upload_${fileId}`);
    } catch (error) {
      console.error('Error clearing upload state:', error);
    }
  }
  
  /**
   * Get upload status by file ID
   * @param {string} fileId - File ID to check
   * @returns {Promise<Object>} File status object
   */
  static async getFileStatus(fileId) {
    try {
      const response = await axios.get(`${API_URL}/status/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting file status:', error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to get file status. Please try again.'
      );
    }
  }
  
  /**
   * Get MIME type from filename
   * @param {string} filename - Filename with extension
   * @returns {string} MIME type
   */
  static getMimeTypeFromFilename(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    const mimeTypes = {
      'mp4': 'video/mp4',
      'mov': 'video/quicktime',
      'avi': 'video/x-msvideo',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',
      'mkv': 'video/x-matroska',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }
}

export default UploadService;